import {WorkforceProfileWizardResearchStep} from '../WorkforceProfileWizardResearchStep';
import {WorkforceProfileWizardReviewStep} from '../WorkforceProfileWizardReviewStep';

import {WorkforceProfileWizardCompetenciesStep} from './WorkforceProfileWizardCompetenciesStep';
import {WorkforceProfileWizardEducationStep} from './WorkforceProfileWizardEducationStep';
import {WorkforceProfileWizardExperienceStep} from './WorkforceProfileWizardExperienceStep';
import {WorkforceProfileWizardExtrasStep} from './WorkforceProfileWizardExtrasStep';
import {WorkforceProfileWizardKnowledgeStep} from './WorkforceProfileWizardKnowledgeStep';
import {WorkforceProfileWizardLanguagesStep} from './WorkforceProfileWizardLanguagesStep';
import {WorkforceProfileWizardResponsibilitiesStep} from './WorkforceProfileWizardResponsibilitiesStep';
import {WorkforceProfileWizardSetupStep} from './WorkforceProfileWizardSetupStep';
import type {WorkforceProfileCandidateField} from './workforceProfileWizardStepTypes';
import {WorkforceProfileWizardSummaryStep} from './WorkforceProfileWizardSummaryStep';
import {SupportedLanguages} from '../../../../../../types/SupportedLanguages';
import {Department} from '../../../../profile/types';
import {DepartmentTargetProfileAssignment} from '../../../types/DepartmentTargetProfileTypes';
import type {WorkforceProfileAnalysisDataInput} from '../../../types/workforceProfileDetailsTypes';
import type {
  WorkforceProfileGuidedStepId,
  WorkforceProfileResearchLookup,
  WorkforceProfileWizardSetup,
  WorkforceProfileWizardState,
} from '../../../types/workforceProfileWizardTypes';

export interface Props {
  stepId: WorkforceProfileGuidedStepId;
  departments: Department[];
  currentLanguage: SupportedLanguages;
  wizardState: WorkforceProfileWizardState;
  analysisInput: WorkforceProfileAnalysisDataInput;
  departmentTargetProfiles: DepartmentTargetProfileAssignment[];
  isTargetProfilesLoading: boolean;
  isTargetProfilesError: boolean;
  isResearchLoading: boolean;
  reviewIssues: string[];
  onSetupChange: (setup: WorkforceProfileWizardSetup) => void;
  onLookupChange: (lookup: WorkforceProfileResearchLookup) => void;
  onAnalysisInputChange: (input: WorkforceProfileAnalysisDataInput) => void;
  onCandidateFieldChange: (field: WorkforceProfileCandidateField, value: string) => void;
  onGenerateSuggestions: () => void;
  onEditStep: (stepId: WorkforceProfileGuidedStepId) => void;
}

export const WorkforceProfileWizardStepContent = ({
  stepId,
  departments,
  currentLanguage,
  wizardState,
  analysisInput,
  departmentTargetProfiles,
  isTargetProfilesLoading,
  isTargetProfilesError,
  isResearchLoading,
  reviewIssues,
  onSetupChange,
  onLookupChange,
  onAnalysisInputChange,
  onCandidateFieldChange,
  onGenerateSuggestions,
  onEditStep,
}: Props) => {
  const {setup, lookup} = wizardState;
  const profile = analysisInput.candidateProfile ?? {};

  switch (stepId) {
    case 'setup':
      return (
        <WorkforceProfileWizardSetupStep
          departments={departments}
          currentLanguage={currentLanguage}
          setup={setup}
          lookup={lookup}
          departmentTargetProfiles={departmentTargetProfiles}
          isTargetProfilesLoading={isTargetProfilesLoading}
          isTargetProfilesError={isTargetProfilesError}
          onSetupChange={onSetupChange}
          onLookupChange={onLookupChange}
        />
      );
    case 'research':
      return (
        <WorkforceProfileWizardResearchStep
          lookup={lookup}
          analysisInput={analysisInput}
          isResearchLoading={isResearchLoading}
          researchCompleted={wizardState.researchCompleted}
          onLookupChange={onLookupChange}
          onGenerateSuggestions={onGenerateSuggestions}
        />
      );
    case 'summary':
      return (
        <WorkforceProfileWizardSummaryStep
          summary={profile.summary ?? ''}
          onSummaryChange={(value) => onCandidateFieldChange('summary', value)}
        />
      );
    case 'responsibilities':
      return (
        <WorkforceProfileWizardResponsibilitiesStep
          responsibilities={profile.responsibilities ?? ''}
          senioritySignals={profile.senioritySignals ?? ''}
          onResponsibilitiesChange={(value) => onCandidateFieldChange('responsibilities', value)}
          onSenioritySignalsChange={(value) => onCandidateFieldChange('senioritySignals', value)}
        />
      );
    case 'experience':
      return (
        <WorkforceProfileWizardExperienceStep
          workExperiences={analysisInput.workExperiences}
          onWorkExperiencesChange={(rows) => onAnalysisInputChange({...analysisInput, workExperiences: rows})}
        />
      );
    case 'competencies':
      return (
        <WorkforceProfileWizardCompetenciesStep
          rows={analysisInput.skillsCompetencies}
          suggested={wizardState.suggestedFieldKeys.includes('competencies')}
          onRowsChange={(rows) => onAnalysisInputChange({...analysisInput, skillsCompetencies: rows})}
        />
      );
    case 'knowledge':
      return (
        <WorkforceProfileWizardKnowledgeStep
          rows={analysisInput.knowledgeSkills}
          suggested={wizardState.suggestedFieldKeys.includes('knowledge')}
          onRowsChange={(rows) => onAnalysisInputChange({...analysisInput, knowledgeSkills: rows})}
        />
      );
    case 'education':
      return (
        <WorkforceProfileWizardEducationStep
          education={profile.education ?? ''}
          onEducationChange={(value) => onCandidateFieldChange('education', value)}
        />
      );
    case 'languages':
      return (
        <WorkforceProfileWizardLanguagesStep
          rows={analysisInput.languages}
          onRowsChange={(rows) => onAnalysisInputChange({...analysisInput, languages: rows})}
        />
      );
    case 'extras':
      return (
        <WorkforceProfileWizardExtrasStep
          certifications={analysisInput.certifications}
          workingStyleNotes={profile.workingStyleNotes ?? ''}
          industryExperience={profile.industryExperience ?? ''}
          onCertificationsChange={(values) => onAnalysisInputChange({...analysisInput, certifications: values})}
          onWorkingStyleNotesChange={(value) => onCandidateFieldChange('workingStyleNotes', value)}
          onIndustryExperienceChange={(value) => onCandidateFieldChange('industryExperience', value)}
        />
      );
    case 'review':
      return (
        <WorkforceProfileWizardReviewStep
          analysisInput={analysisInput}
          reviewIssues={reviewIssues}
          onEditStep={onEditStep}
        />
      );
    default:
      return null;
  }
};
